/**
 * ServiceLocator - Dependency injection container for managers
 * Replaces Godot's autoload singleton pattern
 */

type ServiceMap = Map<string, unknown>;

class ServiceLocatorClass {
  private services: ServiceMap = new Map();
  private initialized: boolean = false;

  /**
   * Register a service with a key
   */
  register<T>(key: string, service: T): void {
    if (this.services.has(key)) {
      console.warn(`ServiceLocator: Overwriting existing service '${key}'`);
    }
    this.services.set(key, service);
  }

  /**
   * Get a service by key
   * @throws Error if service not found
   */
  get<T>(key: string): T {
    const service = this.services.get(key);
    if (service === undefined) {
      throw new Error(`ServiceLocator: Service '${key}' not found. Did you forget to register it?`);
    }
    return service as T;
  }

  /**
   * Try to get a service, returns undefined if not found
   */
  tryGet<T>(key: string): T | undefined {
    return this.services.get(key) as T | undefined;
  }

  /**
   * Check if a service is registered
   */
  has(key: string): boolean {
    return this.services.has(key);
  }

  /**
   * Remove a service
   */
  unregister(key: string): boolean {
    return this.services.delete(key);
  }

  /**
   * Clear all services
   */
  clear(): void {
    this.services.clear();
    this.initialized = false;
  }

  /**
   * Mark as initialized (all core services registered)
   */
  markInitialized(): void {
    this.initialized = true;
  }

  /**
   * Check if all core services are initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Get list of all registered service keys
   */
  getRegisteredServices(): string[] {
    return Array.from(this.services.keys());
  }
}

// Export singleton instance
export const ServiceLocator = new ServiceLocatorClass();

// Service key constants for type safety
export const Services = {
  // MMO Services
  MMO_STATE: 'mmoState',
  MMO_NETWORK: 'mmoNetwork',
  MMO_PROGRESSION: 'mmoProgression',
  MMO_MONETIZATION: 'mmoMonetization',
  // Wallet & Payments
  WALLET: 'wallet',
  PAYMENT_FLOW: 'paymentFlow',
  // Memory Management
  MEMORY: 'memory',
  // Audio
  AUDIO: 'audio',
  // Internationalization
  I18N: 'i18n',
} as const;

export type ServiceKey = (typeof Services)[keyof typeof Services];
